<template>
  <v-card flat class="site-systems-bar">
    <v-app-bar elevation="1" class="px-11">
      <v-app-bar-title class="text-primary text-h6 font-weight-bold">
        {{ label }}
      </v-app-bar-title>
      <v-row>
        <v-col>
          <SiteAutocomplete
            class="mt-6 mr-5"
            :items="sitesForSelector"
            v-model="selectedSite"
            @update:modelValue="initializeSiteSelection"
          />
        </v-col>
        <v-col>
          <v-text-field
            class="mt-6 mr-5"
            v-model="searchTerm"
            hide-details
            prepend-inner-icon="mdi-magnify"
            bg-color="white"
            single-line
            variant="outlined"
            density="compact"
            :placeholder="placeholder"
            :clearable="true"
            @click:clear="handleClearSearch"
            @update:modelValue="handleSearchInput"
          />
        </v-col>
      </v-row>
      <v-btn
        v-if="userCanCreate"
        class="rounded-2 mr-3"
        color="primary"
        variant="flat"
        size="large"
        @click="handleAddSiteSystem"
      >
        <div class="text-subtitle-2 text-white text-truncate font-weight-bold">
          Add Site System
        </div>
      </v-btn>
      <v-spacer />
    </v-app-bar>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { useAbility } from '@/plugins/useAbility.ts';
import type { ISite } from '@/Core.Service.Domain/Sites/SiteSystems/types/index.ts';
import SiteAutocomplete from '@/Core.UI.Domain/Components/SiteAutocomplete.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const ability = useAbility();

const searchTerm = ref('');
const selectedSite = ref<ISite | null>(null);

const sitesForSelector = computed<ISite[]>(
  () => store.getters['SitesStore/SiteSystemsStore/sitesForSelector']
);
const defaultSite = computed<ISite | null>(
  () => store.getters['session/defaultSite']
);

const placeholder = computed<string>(
  () => (route.meta?.placeholder as string) || 'Search for a System'
);
const label = computed<string>(
  () => (route.meta?.label as string) || 'Site Systems'
);
const userCanCreate = computed(() => ability.can('create', 'analytics'));

function setSearchTerm(term: string): void {
  store.commit('SitesStore/SiteSystemsStore/SET_SITE_SYSTEM_SEARCH_TERM', term);
}

function clearSearchTerm(): void {
  searchTerm.value = '';
  setSearchTerm('');
}

function initializeSiteSelection(site: ISite | null): void {
  selectedSite.value = site || sitesForSelector.value[0] || null;

  if (!selectedSite.value) return;

  const siteId: number =
    (selectedSite.value as ISite & { siteId?: number }).siteId ??
    (selectedSite.value as ISite).id ??
    0;

  store
    .dispatch('SitesStore/SiteSystemsStore/setSiteSystems', siteId)
    .then(() => {
      store.dispatch('session/setDefaultSite', selectedSite.value);
      clearSearchTerm();
    });
}

function handleAddSiteSystem(): void {
  router.push({ name: 'SiteSystemsCreate' });
}

function handleSearchInput(value: string): void {
  setSearchTerm(value);
}

function handleClearSearch(): void {
  clearSearchTerm();
}

watch(route, () => {
  initializeSiteSelection(defaultSite.value);
  clearSearchTerm();
});

watch(selectedSite, (site) => {
  if (site) {
    store.dispatch('session/setDefaultSite', site);
  }
});

store.dispatch('SitesStore/SiteSystemsStore/setSitesForSelector').then(() => {
  initializeSiteSelection(defaultSite.value);
});
</script>

<style lang="scss" scoped>
.site-systems-bar {
  z-index: 1;
}

.v-input {
  max-width: 700px !important;
}

:deep(.v-field__input) {
  color: $primary !important;
}

:deep(.bg-white) {
  color: $primary !important;
}
</style>
